import { Card, CardBody, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { BiMenu } from 'react-icons/bi';
import { RiBook2Line, RiStackLine } from 'react-icons/ri';

import { PortfolioDrawer, usePortfolioDrawerControls } from '@/cip/Portfolio/PortfolioDrawer/PortfolioDrawer';

import { LotType, OpenLot, ResourceType } from '../_openapi/portfolio-optimizer';
import { useOpenLots } from '../api/lot';
import { AssetDrawer, useAssetDrawerControls } from '../Asset/AssetDrawer/AssetDrawer';
import { Pagination } from '../widgets/Table/Pagination';
import { Table } from '../widgets/Table/Table';
import { TableControls } from '../widgets/Table/TableControls';
import { usePersistentTableState } from '../widgets/Table/usePersistentTableState';
import { useTableColumnSettings } from '../widgets/Table/useTableColumnSettings';
import { openLotListColumns } from './openLotListColumns';

const columnHelper = createColumnHelper<OpenLot>();
export const OpenLotList = () => {
    const { viewAsset, ...assetDrawerProps } = useAssetDrawerControls();
    const { viewPortfolio, ...portfolioDrawerProps } = usePortfolioDrawerControls();

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'contextMenu',
                cell: ({ row }) => {
                    return (
                        <Menu isLazy>
                            <MenuButton
                                size="xs"
                                as={IconButton}
                                aria-label="Options"
                                icon={<Icon boxSize={3} as={BiMenu} />}
                                w="20px"
                                minW="20px"
                                h="20px"
                                variant="outline"
                                borderRadius="sm"
                            />
                            <Portal>
                                <MenuList>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={RiBook2Line} />}
                                        onClick={() => viewPortfolio(row.original.pId)}
                                    >
                                        Portfolio details
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={RiStackLine} />}
                                        onClick={() => viewAsset(row.original.sId)}
                                    >
                                        Security details
                                    </MenuItem>
                                </MenuList>
                            </Portal>
                        </Menu>
                    );
                },
                enableSorting: false,
                meta: {
                    isDisplayCol: true,
                    isSticky: true,
                },
            }),
            ...openLotListColumns,
        ],
        [viewAsset, viewPortfolio],
    );

    const { pagination, setPagination, sorting, setSorting, resetTable, columnFilters, setColumnFilters } =
        usePersistentTableState(columns);

    const { data: openLotsPage, isFetching } = useOpenLots({ pagination, columnFilters, sorting });

    const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder } = useTableColumnSettings('openLot');
    const table = useReactTable({
        data: openLotsPage?.lots || [],
        columns,
        state: {
            columnVisibility,
            columnOrder,
            pagination,
            sorting,
            columnFilters,
        },
        initialState: {
            columnPinning: {
                left: ['contextMenu'],
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <Card>
                <CardBody>
                    <TableControls
                        table={table}
                        onReset={resetTable}
                        resourceType={ResourceType.LOT}
                        metaData={{ lotType: LotType.OPEN }}
                    />
                    <Table table={table} isLoading={isFetching} allowXOverflow />
                    <Pagination table={table} totalItems={openLotsPage?.totalNr} />
                </CardBody>
            </Card>
            <PortfolioDrawer {...portfolioDrawerProps} />
            <AssetDrawer {...assetDrawerProps} />
        </>
    );
};
